import {
    createContext,
    type FC,
    type PropsWithChildren,
    type ReactNode,
    type RefObject,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';
import { type Schema } from 'ts-json-schema-generator';
import { IconInfo, type PopupProps } from '@direct-frontend/components';
import { noop, useToggle } from '@direct-frontend/stdlib';
import { type OpenApiSchemaSectionTemplate } from '#shared/api/schema';
import { Tooltip } from '#shared/ui/Tooltip';
import { useConstructorAddSectionPopup } from '../../hooks/useConstructorAddSectionPopup';
import { type CopiedSection, useConstructorCopiedSection } from '../../hooks/useConstructorCopiedSection';
import { useConstructorDeleteSection } from '../../hooks/useConstructorDeleteSection';
import { useConstructorDeleteTemplate } from '../../hooks/useConstructorDeleteTemplate';
import { useConstructorDetachSection } from '../../hooks/useConstructorDetachSection';
import { useConstructorEditCustomSection } from '../../hooks/useConstructorEditCustomSection';
import { useConstructorHideSection } from '../../hooks/useConstructorHideSection';
import { type ConstructorLimitType, useConstructorLimits } from '../../hooks/useConstructorLimits';
import { useConstructorReplaceSection } from '../../hooks/useConstructorReplaceSection';
import { useConstructorShowSection } from '../../hooks/useConstructorShowSection';
import { type ConstructorRightBarSectionType } from '../../model/slices/types';
import styles from './ConstructorProvider.module.css';

interface ConstructorContextType {
    saveButtonRef: RefObject<HTMLButtonElement> | undefined;
    showSaveButtonTooltip: (text: ReactNode) => void;
    detachSection: (pageId: string, sectionInstanceId: string) => void;
    deleteSection: (pageId: string, sectionInstanceId: string) => void;
    hideSection: (pageId: string, sectionInstanceId: string) => void;
    showSection: (pageId: string, sectionInstanceId: string) => void;
    deleteTemplate: (templateId: string) => void;
    copiedSection: CopiedSection | null;
    copySection: (sectionToCopy: ConstructorRightBarSectionType) => void;
    checkCopiedSection: VoidFunction;
    areHiddenPagesShown: boolean;
    setAreHiddenPagesShown: (value: boolean) => void;
    areHiddenSectionsShown: boolean;
    setAreHiddenSectionsShown: (value: boolean) => void;
    editCustomSection: (templateId: string) => void;
    openAddSectionPopup: (options: {
        newDisplaySequence: number;
        widgetToReplace?: string;
        addCallback?: (settings: Object) => void;
    }) => void;
    closeAddSectionPopup: VoidFunction;
    addSectionPopupRef: RefObject<HTMLDivElement> | undefined;
    replaceSection: (props: { pageId: string; sectionInstanceId: string; newSectionSettings: Object }) => void;
    showLimit: (limit: ConstructorLimitType) => void;
    checkForLimits: (limit: ConstructorLimitType) => ConstructorLimitType | null;
    limits: Record<ConstructorLimitType, number>;
}

const ConstructorContext = createContext<ConstructorContextType>({
    saveButtonRef: undefined,
    showSaveButtonTooltip: noop,
    detachSection: noop,
    deleteSection: noop,
    hideSection: noop,
    showSection: noop,
    deleteTemplate: noop,
    copiedSection: null,
    copySection: noop,
    checkCopiedSection: noop,
    areHiddenPagesShown: false,
    setAreHiddenPagesShown: noop,
    areHiddenSectionsShown: false,
    setAreHiddenSectionsShown: noop,
    editCustomSection: noop,
    openAddSectionPopup: noop,
    closeAddSectionPopup: noop,
    addSectionPopupRef: undefined,
    replaceSection: noop,
    showLimit: noop,
    checkForLimits: () => null,
    limits: {
        page: 0,
        section: 0,
        module: 0,
    },
});

export function useConstructorContext() {
    const context = useContext(ConstructorContext);

    if (!context) {
        throw new Error('"useConstructorContext" hook  is called out of ConstructorProvider');
    }

    return context;
}

const SAVE_BUTTON_TOOLTIP_TIMEOUT = 10_000;

export const ConstructorProvider: FC<
    PropsWithChildren<{
        renderCustomSectionModal: (props: { isOpen: boolean; onClose: VoidFunction; templateId: string }) => ReactNode;
        renderDeleteTemplateModal: (props: {
            isOpen: boolean;
            onClose: VoidFunction;
            onDelete: VoidFunction;
            templateId: string;
        }) => ReactNode;
        renderAddSectionPopup: (
            props: PopupProps & {
                onClick: (props: {
                    widget: string;
                    newDisplaySequence: number;
                    template?: OpenApiSchemaSectionTemplate;
                }) => void;
                widgetToReplace?: string;
                newDisplaySequence: number;
            }
        ) => ReactNode;
        getDefaultModulesSettings: (schema: Schema) => Object;
        limitSettings: {
            pagesLimit?: number;
            sectionsLimit?: number;
            modulesLimit?: number;
        };
    }>
> = ({
    renderDeleteTemplateModal,
    renderCustomSectionModal,
    renderAddSectionPopup,
    getDefaultModulesSettings,
    limitSettings,
    children,
}) => {
    const saveButtonRef = useRef<HTMLButtonElement>(null);
    const {
        value: isSaveButtonTooltipOpen,
        toggleOn: openSaveButtonTooltip,
        toggleOff: closeSaveButtonTooltip,
    } = useToggle(false);

    const [saveButtonText, setSaveButtonText] = useState<ReactNode>('');
    const [areHiddenPagesShown, setAreHiddenPagesShown] = useState(false);
    const [areHiddenSectionsShown, setAreHiddenSectionsShown] = useState(false);
    const saveButtonTimeoutRef = useRef<TimeoutType>();

    const { detachSection, renderConfirmModal: renderDetachSectionModal } = useConstructorDetachSection();
    const { deleteSection, renderConfirmModal: renderDeleteSectionModal } = useConstructorDeleteSection();
    const { hideSection, renderConfirmModal: renderHideSectionModal } = useConstructorHideSection();
    const { showSection, renderConfirmModal: renderShowSectionModal } = useConstructorShowSection();
    const { copySection, checkCopiedSection, copiedSection } = useConstructorCopiedSection();
    const { editCustomSection, renderModal: renderCustomSectionEditorModal } = useConstructorEditCustomSection({
        renderCustomSectionModal,
    });
    const { deleteTemplate, renderConfirmDeleteTemplateModal } = useConstructorDeleteTemplate({
        renderDeleteTemplateModal,
    });
    const { replaceSection, renderReplaceConfirmModal } = useConstructorReplaceSection();
    const {
        showLimit,
        checkForLimits,
        renderConfirmModal: renderLimitModal,
        limits,
    } = useConstructorLimits(limitSettings);

    const {
        openPopup: openAddSectionPopupCallback,
        renderPopup: renderConstructorAddSectionPopup,
        addSectionPopupRef,
        closePopup: closeAddSectionPopup,
    } = useConstructorAddSectionPopup({
        renderAddSectionPopup,
        getDefaultModulesSettings,
    });

    const openAddSectionPopup = useCallback(
        (options: {
            newDisplaySequence: number;
            widgetToReplace?: string;
            addCallback?: (settings: Object) => void;
        }) => {
            const limit = checkForLimits('section');

            if (limit) {
                showLimit(limit);

                return;
            }

            openAddSectionPopupCallback(options);
        },
        [openAddSectionPopupCallback, showLimit, checkForLimits]
    );

    const showSaveButtonTooltip = useCallback(
        (text: ReactNode) => {
            if (!saveButtonRef.current) {
                return;
            }

            clearTimeout(saveButtonTimeoutRef.current);
            openSaveButtonTooltip();
            setSaveButtonText(text);
            saveButtonTimeoutRef.current = setTimeout(() => {
                closeSaveButtonTooltip();
                setSaveButtonText('');
            }, SAVE_BUTTON_TOOLTIP_TIMEOUT);
        },
        [closeSaveButtonTooltip, openSaveButtonTooltip]
    );

    const context = {
        saveButtonRef,
        showSaveButtonTooltip,
        detachSection,
        deleteSection,
        hideSection,
        showSection,
        deleteTemplate,
        copiedSection,
        copySection,
        checkCopiedSection,
        areHiddenPagesShown,
        setAreHiddenPagesShown,
        areHiddenSectionsShown,
        setAreHiddenSectionsShown,
        editCustomSection,
        openAddSectionPopup,
        closeAddSectionPopup,
        addSectionPopupRef,
        replaceSection,
        showLimit,
        checkForLimits,
        limits,
    };

    useEffect(
        () => () => {
            clearTimeout(saveButtonTimeoutRef.current);
        },
        []
    );

    return (
        <ConstructorContext.Provider value={context}>
            {children}
            <Tooltip
                triggerRef={saveButtonRef}
                isOpen={isSaveButtonTooltipOpen}
                color="info"
                iconLeft={<IconInfo className={styles.tooltipIcon} color="white" size="20" role="main" />}
                title={saveButtonText}
                maxWidth="360"
            />
            {renderDetachSectionModal()}
            {renderDeleteSectionModal()}
            {renderHideSectionModal()}
            {renderShowSectionModal()}
            {renderCustomSectionEditorModal()}
            {renderConfirmDeleteTemplateModal()}
            {renderConstructorAddSectionPopup()}
            {renderReplaceConfirmModal()}
            {renderLimitModal()}
        </ConstructorContext.Provider>
    );
};
